import axios from 'axios';
import { call, all, put, takeLatest, select } from 'redux-saga/effects';

import * as clientChecklistActions from './client-checklist-actions';
import clientChecklistService from './client-checklist-service';

import {
  DELETE_ITEM,
  EDIT_ITEM,
  GET_BY_ID_REQUEST,
  GET_LIST_ALL_QUESTIONS_REQUEST,
  SAVE_CLIENT_CHECKLIST_REQUEST
} from './client-checklist-constants';

import { ISA_ERROR } from '~/constants';

import { AppStateType } from '../root-reducer';

import { formatMessage } from '~/i18n/translate';

import history from '~/services/history';
import notification from '~/services/notification';

let cancel: any;
const CancelToken = axios.CancelToken;

const INITIAL_VALUES = {
  required: false,
  questions: [
    {
      question: '',
      typeAnswer: 'string',
      answers: [{ answer: '' }, { answer: '' }],
      photoRequired: false
    }
  ]
};

export function* getAllClientChecklist() {
  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(clientChecklistActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select((state: AppStateType) => state.clientChecklist.list);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const [{ data: sellers }, { data: clientChecklist }, { data: count }] = yield all([
      call(clientChecklistService.getSellers),
      call(
        clientChecklistService.getListClientChecklist,
        {
          ...pagination
        },
        {
          cancelToken
        }
      ),
      call(clientChecklistService.getListClientChecklistCount, { ...pagination }, { cancelToken })
    ]);

    const parsedData = clientChecklist?.map(item => ({
      ...item,
      createdBy: sellers?.find(seller => seller?.externalCode === item?.userExternalCode)
    }));

    yield put(
      clientChecklistActions.getAllChecklistSuccess({
        checklist: parsedData,
        rowsCount: count || 0
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientChecklistActions.setLoadingData(false));
  }
}

export function* getById({ id }: any) {
  try {
    yield put(clientChecklistActions.setLoadingData(true));

    if (id) {
      const { data: questions } = yield call(clientChecklistService.getByIdClientChecklist, id);

      const parsedQuestions = questions?.type?.map(item => ({
        question: item?.question,
        answers:
          item?.questionType === 'checkbox'
            ? item?.fields?.map(answer => ({
                answer: answer?.option,
                value: answer?.fieldCode
              }))
            : [{ answer: '' }, { answer: '' }],
        typeAnswer: item?.questionType === 'checkbox' ? 'boolean' : 'string',
        photoRequired: item?.photoRequired
      }));

      const parsedData = {
        questions: parsedQuestions,
        required: questions?.required,
        questionaryName: questions?.questionaryName,
        clientType: questions?.clientType ?? null
      };

      yield put(
        clientChecklistActions.getByIdSuccess({
          data: parsedData
        })
      );
    } else {
      yield put(
        clientChecklistActions.getByIdSuccess({
          data: INITIAL_VALUES
        })
      );
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientChecklistActions.setLoadingData(false));
  }
}

export function* saveClientChecklist({ payload, id }: any) {
  try {
    yield put(clientChecklistActions.setLoadingSend(true));

    const { questions, required, questionaryName } = payload;

    const parsedQuestions = id
      ? questions?.map(item => ({
          photoRequired: item?.photoRequired,
          newQuestion: item?.question,
          newFields: item?.typeAnswer === 'boolean' ? item?.answers?.map(answer => answer?.answer) : undefined
        }))
      : questions?.map(item => ({
          photoRequired: item?.photoRequired,
          question: item?.question,
          fields: item?.typeAnswer === 'boolean' ? item?.answers?.map(answer => answer?.answer) : undefined
        }));

    const parsedData = {
      questions: parsedQuestions,
      required: required ?? false,
      clientType: payload?.clientType ?? null,
      questionaryName
    };

    if (id) {
      yield call(clientChecklistService.editClientChecklist, {
        id,
        data: parsedData
      });
    } else {
      yield call(clientChecklistService.saveClientChecklist, parsedData);
    }

    yield call(
      notification.success,
      id ? formatMessage('checklistClientUpdatedSuccess') : formatMessage('checklistClientCreatedSuccess')
    );

    yield call(history.push, `/register/client-checklist`);
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(clientChecklistActions.setLoadingSend(false));
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/client-checklist/edit/${id}`);
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('checklistClientExcludeDeleteMessage')
    );
    if (isConfirmed) {
      yield put(clientChecklistActions.setLoadingSend(true));
      yield call(clientChecklistService.deleteClientChecklist, id);
      yield put(clientChecklistActions.setLoadingSend(false));

      yield put(clientChecklistActions.getAllChecklistRequest());
      yield call(notification.success, formatMessage('checklistClientExcludeSuccess'));
    }
  } catch (error) {
    yield put(clientChecklistActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export default all([
  takeLatest(GET_LIST_ALL_QUESTIONS_REQUEST, getAllClientChecklist),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_CLIENT_CHECKLIST_REQUEST, saveClientChecklist),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(DELETE_ITEM, deleteItem)
]);
